
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from '../../../common/utils/withRouter'
import _ from 'lodash'
import classNames from 'classnames'
import BigBreadcrumbs from '../../../components/layout/navigation/components/BigBreadcrumbs'
import WidgetGrid from '../../../components/layout/widgets/WidgetGrid'
import JarvisWidget from '../../../components/layout/widgets/JarvisWidget'
import { createStatusBadgeString } from '../../../common/components/diamond/StatusBadge'
import {
  fetchDiamonds,
  clearDiamonds,
  clearSelectedDiamond
} from '../../../common/actions/diamond'
import { getRetailer } from '../../../common/actions/retailer'
import { isStandardRetailer } from '../../../common/utils/retailer'
import {
  statusCodes,
  jewelryTypes,
  settings
} from '../../../common/utils/diamond'
import imageUrl from '../../../common/utils/imageUrl'
import { LIST_PAGE_LENGTH, SERVER } from '../../../common/constants'
import UpgradeAccountButton from '../../../common/components/common/UpgradeAccountButton'
import ClearFormFieldButton from '../../../common/components/common/ClearFormFieldButton'
import EmptyTableDiamonds from '../../../common/components/diamond/EmptyTableDiamonds'

import styles from './List.module.css'
import DataTable from '../../../components/tables/Datatable'
import api from '../../../common/api'
import defaultDiamondImage from '../../../assets/images/image-coming-soon.jpg'

// Gemstones and jewelry come back with cut/clarity/color as null. A renderer
// that throws aborts the whole DataTables draw and leaves the table empty, so
// treat null like 'not-applicable'.
const isNA = value =>
  value == null || value === '' || value === 'not-applicable'
const renderCarat = carat => (isNA(carat) || carat <= 0 ? 'N/A' : carat)
const renderTitle = title =>
  isNA(title) ? 'N/A' : String(title).split('-').map(_.capitalize).join(' ')
const renderClarity = clarity =>
  isNA(clarity) ? 'N/A' : String(clarity).replace(/-/g, ' ').toUpperCase()
const renderUpper = value => (isNA(value) ? 'N/A' : _.toUpper(value))
const renderColor = value =>
  isNA(value) ? 'N/A' : renderUpper(String(value).replace(/-/g, ' '))

const renderImage = img => `
  <div class="${styles.imageColumn}">
    <div class="${styles.expandButton}">+</div>
    <img src="${imageUrl('inventory', img)}" onerror="this.onerror=null; this.src='${defaultDiamondImage}';" />
  </div>
`

const renderInternalId = internalId => `
  <div class="${styles.internalIdColumn}">
    <div>${internalId}</div>
  </div>
`

const renderReviewMessage = message => `
  <div class="${styles.reviewMessageColumn}">
    ${message}
  </div>
`

const isDifferentPage = (currentProps, nextProps) =>
  currentProps.location.query.page !== nextProps.location.query.page

const settingFilterNullItem = {
  value: null,
  text: 'All Settings/Jewelry'
}

const settingFilterItems = [
  {
    value: 'loose',
    text: 'Loose Diamonds'
  },
  {
    value: 'gemstone',
    text: 'Gemstones'
  },
  {
    value: 'mounted',
    text: 'Mounted Jewelry'
  },
  {
    value: 'non-diamond-jewelry',
    text: 'Non-Diamond Jewelry'
  },
  {
    value: 'craft-jewelry',
    text: 'Craft Jewelry'
  },
  {
    value: 'service',
    text: 'Service'
  }
].concat(
  Object.keys(jewelryTypes).map(value => ({
    value,
    text: jewelryTypes[value]
  }))
)

class ProductReview extends Component {
  constructor(props) {
    super(props)
    this.state = {
      selectedSetting: settingFilterNullItem
    }
  }
  componentDidMount() {
    const {
      isAdminUser,
      retailer,
      currentUser,
      getRetailer,
      clearSelectedDiamond
    } = this.props
    if (!isAdminUser && !retailer && currentUser) {
      const id = typeof currentUser.get === 'function' ? currentUser.get('id') : currentUser.id
      getRetailer(id)
    }
    clearSelectedDiamond()
    window.reactInstance = this;
    this.handleSpaClick = (e) => {
      const anchor = e.target.closest('a[data-spa-href]')
      if (!anchor) return
      e.preventDefault()
      const href = anchor.getAttribute('data-spa-href')
      const productInfoRaw = anchor.getAttribute('data-product-info')
      if (productInfoRaw) {
        try {
          sessionStorage.setItem('reviewProductInfo', decodeURIComponent(productInfoRaw))
        } catch (err) {}
      }
      this.props.router.push(href)
    }
    this.containerNode && this.containerNode.addEventListener('click', this.handleSpaClick)
  }
  componentDidUpdate(prevProps) {
    const { router, location, diamonds, diamondInProgress } = this.props
    if (
      !diamonds ||
      isDifferentPage(prevProps, this.props) ||
      diamondInProgress
    ) {
      return
    }
    const nextPage = location.query.page
    const page = diamonds.getMeta('pageNumber')
    // Coerce both to strings before comparing — the Laravel API returns
    // current_page as a number while location.query.page is a string, which
    // would otherwise always differ ("1" !== 1) and push the same url on every
    // update until React aborts with "Maximum update depth exceeded".
    if (String(nextPage) !== String(page)) {
      const newLocation = {
        pathname: location.pathname,
        query: Object.assign({}, location.query, { page })
      }
      if (location.query.page) {
        router.push(newLocation)
      } else {
        router.replace(newLocation)
      }
    }
  }
  render() {
    const {
      location,
      isAdminUser,
      retailer,
      diamonds,
      diamondInProgress,
      fetchDiamonds
    } = this.props
    if (!isAdminUser && !retailer && !location.query.page) {
      return null
    }
    let page = parseInt(location.query.page)
    page = _.isInteger(page) ? Math.max(page, 1) : 1
    const displayStart = (page - 1) * LIST_PAGE_LENGTH
    const columns = [
      { data: 'retailer', isHidden: !isAdminUser },
      { data: 'internalId', render: renderInternalId, orderable: false },
      { data: 'ratingValue', className: styles.ratingColumn },
      { data: 'message', render: renderReviewMessage, orderable: false },
      { data: 'createdAt' },
      { data: null, render: this.renderEditButtons, orderable: false }
    ].filter(col => !col.isHidden)
    return (
      <div id="content" className={styles.listWrapper} ref={node => { this.containerNode = node }}>
        <div className="row">
          <BigBreadcrumbs
            items={['Product Review']}
            icon="table"
            className="col-xs-12 col-sm-7 col-md-7 col-lg-4"
          />
          {this.renderActionButtons()}
        </div>
        {diamonds &&
          diamonds.size() === 0 &&
          !diamonds.getMeta('query') &&
          !diamondInProgress
          ? <EmptyTableDiamonds />
          : null}
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                fullscreenbutton={false}
                deletebutton={false}
                color="darken"
              >
                <header>
                  <span className="widget-icon">
                    <i className="fa fa-list" />
                  </span>
                  <h2>Items</h2>
                </header>
                <div>
                  <div className="widget-body no-padding">
                    <DataTable
                      options={{
                        processing: true,
                        serverSide: true,
                        ajax: (query, callback) => {
                          fetchDiamonds(
                            this.state.selectedSetting,
                            query,
                            callback
                          )
                        },
                        lengthChange: false,
                        pageLength: LIST_PAGE_LENGTH,
                        displayStart,
                        search: {
                          search: location.query.query
                        },
                        order: [[columns.findIndex(col => col.data === 'createdAt'), 'desc']],
                        columns,
                        drawCallback: function () {
                          const table = $(this.api().table().node());

                          // Attach click event handler
                          table.find('.view-product-review').off('click').on('click', function () {
                            const rowId = $(this).data('id');
                            const rowData = table
                              .DataTable()
                              .row($(this).closest('tr'))
                              .data(); // Get the data for the clicked row

                            // Row ID and data available for navigation

                            if (window.reactInstance) {
                              // Perform navigation or other actions
                              history.push(`/product-review/${rowData.id}`)
                            }
                          });
                        },
                        responsive: { details: { type: 'inline' } },
                      }}
                      searchPlaceholder={`Retailer`}
                      className={classNames(
                        'table table-striped table-bordered table-hover',
                        styles.table
                      )}
                      width="100%"
                      ref={node => {
                        this.cachedDatatable = node && node.cachedDatatable
                      }}
                    >
                      <thead>
                        <tr>
                          {isAdminUser && <th>Retailer</th>}
                          <th>Product ID</th>
                          <th width="100" className={styles.ratingColumn}>Latest Rating</th>
                          <th>Latest Review</th>
                          <th>Date</th>
                          <th className={styles.actionsColumn}>Actions</th>
                        </tr>
                      </thead>
                    </DataTable>
                    {/* {this.renderSettingFilter()} */}
                  </div>
                </div>
              </JarvisWidget>
            </article>
            <div className={styles.sectionHelp}>
              <i className="fa fa-question-circle" />
              <div className={styles.sectionHelpBlurb}>
                <span>
                  <a
                    href="https://intercom.help/gemsby/selling-on-gemsby"
                    target="_blank"
                  >
                    Gemsby Help Center
                  </a>
                </span>
                <a
                  href="https://intercom.help/gemsby/selling-on-gemsby/preparing-your-store/tips-on-preparing-your-store"
                  target="_blank"
                >
                  <strong>Tips on Preparing Your Store</strong>
                </a>
              </div>
            </div>
          </div>
        </WidgetGrid>
      </div>
    )
  }
  renderActionButtons() {
    const { router, isAdminUser, retailer, diamonds } = this.props
    if (!diamonds) {
      return null
    }
    if (isAdminUser || isStandardRetailer(retailer)) {
      return (
        <div className={styles.actionButtons}>
          {/* {isAdminUser &&
            <button
              className={`btn btn-primary ${styles.actionButton}`}
              onClick={() => {
                router.push('/inventory/csvUpload')
              }}
            >
              Bulk Upload (CSV File)
            </button>}
          {this.renderAddButton()} */}
        </div>
      )
    }
    if (diamonds.data.length < 2) {
      return (
        <div className={styles.actionButtons}>
          {this.renderAddButton()}
        </div>
      )
    }
    return (
      <div className={styles.actionButtons}>
        <div className={styles.upgradeMessage}>
          You have reached the account limit, upgrade to{' '}
          <strong>Standard Account</strong> to upload more inventory.
        </div>
        <UpgradeAccountButton className={styles.actionButton} />
      </div>
    )
  }
  renderAddButton() {
    return (
      <button
        className={`btn btn-primary ${styles.actionButton}`}
        onClick={() => {
          this.props.router.push('/inventory/new')
        }}
      >
        Add an Item
      </button>
    )
  }
  renderSettingFilter() {
    return (
      <div className={styles.settingFilter}>
        <div className={classNames('input-group', styles.settingFilterField)}>
          <span className="input-group-addon input-sm">
            <i className="glyphicon glyphicon-filter" />
          </span>
          <div
            className={classNames(
              'form-control',
              'input-sm',
              styles.settingFilterDropdown
            )}
          >
            <button
              type="button"
              className="dropdown-toggle"
              data-toggle="dropdown"
            >
              <span>
                {this.state.selectedSetting.text}
              </span>
              <span className="caret" />
            </button>
            <ul className="dropdown-menu">
              {settingFilterItems.map(item =>
                <li key={item.value}>
                  <a href="#" onClick={this.onFilterSelect(item)}>
                    {item.text}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
        {this.state.selectedSetting.value &&
          <ClearFormFieldButton onClick={this.onFilterClear} />}
      </div>
    )
  }
  renderSetting = (setting, type, row) => {
    if (setting === 'mounted' || setting === 'non-diamond-jewelry') {
      const jewelryType = jewelryTypes[row.jewelryType]
      if (jewelryType) {
        return jewelryType
      }
    }
    return settings[setting] || setting
  }
  // componentDidMount() {
  //   // Save React instance globally for routing
  //   window.reactInstance = this;
  // }

  componentWillUnmount() {
    // Cleanup React instance reference
    delete window.reactInstance;
    this.containerNode && this.containerNode.removeEventListener('click', this.handleSpaClick)
  }

  // renderEditButtons = ({ id, statusCode }) => {
  //   const { location, diamonds } = this.props
  //   if (diamonds) {
  //     location.query.query = diamonds.getMeta('query')
  //   }
  //   const backUrl = encodeURIComponent(history.createPath(location))
  //   id = encodeURIComponent(id)



  //   return `
  //   <div>
  //   <a href="#/product-review/${id}">
  //     <button
  //       class="btn btn-primary view-product-review"
  //       data-id="${id}">
  //       View Product Reviews
  //     </button>
  //     </a>
  //   </div>
  // `
  // }
  renderEditButtons = ({ internalId, statusCode, objectID, imageUrl, price, setting, customTitle, retailerName }) => {
    objectID = encodeURIComponent(objectID)
    const productInfo = encodeURIComponent(JSON.stringify({
      internalId: customTitle || internalId || '',
      imageUrl: imageUrl || '',
      price: price || '',
      setting: setting || '',
      statusCode: statusCode || '',
      retailerName: retailerName || ''
    }))
    const liveLink =
      statusCode === statusCodes.DELETED.value
        ? ''
        : ``
    return `<div>
      <a href="/product-review/${objectID}" data-spa-href="/product-review/${objectID}" data-product-info="${productInfo}">
        <button class="btn btn-primary">
          Product Reviews
        </button>
      </a>
      ${liveLink}
    </div>`
  }
  setSettingFilter(item) {
    this.setState({ selectedSetting: item })
    setTimeout(() => {
      this.cachedDatatable.search('').draw()
    })
  }
  onFilterSelect = item => {
    return event => {
      event.preventDefault()
      if (!this.cachedDatatable) {
        return
      }
      this.setSettingFilter(item)
    }
  }
  onFilterClear = event => {
    event.preventDefault()
    if (!this.cachedDatatable) {
      return
    }
    this.props.clearDiamonds()
    this.setSettingFilter(settingFilterNullItem)
  }
}

const mapStateToProps = ({ retailer, diamond, login }) => {
  return {
    retailer: retailer.get('selectedRetailer'),
    diamonds: diamond.get('diamonds'),
    diamondInProgress: diamond.get('inProgress'),
    isAdminUser: login.get('isAdminUser'),
    currentUser: login.get('currentUser')
  }
}

const mapDispatchToProps = (dispatch, nextProps) => {
  return {
    async fetchDiamonds(selectedSetting, query, callback) {
      query.setting = selectedSetting.value;
      // if (query.setting && query.setting !== 'loose') {
      //   query.setting = 'mounted';
      //   query.jewelryType = selectedSetting.value;
      // }
      // DataTables (server-side) sends the current page offset via query.start
      // and query.length. Derive the 1-based page from those. Previously this
      // read location.query.page (the URL), which the pager never updates — so
      // clicking a page always re-fetched the same page.
      const pageLength = parseInt(query.length, 10) || LIST_PAGE_LENGTH
      const start = parseInt(query.start, 10) || 0
      const page = Math.floor(start / pageLength) + 1
      const filter = query.setting || null
      const retailerName = query.search.value || null;

      // Translate the DataTables order (column index + direction) into the
      // backend's sort_by/sort_dir params so sorting happens server-side,
      // across the full result set rather than just the current page.
      const sortFieldMap = {
        retailer: 'retailer',
        ratingValue: 'rating',
        createdAt: 'date'
      }
      const order = query.order && query.order[0]
      const orderedColumn = order && query.columns && query.columns[order.column]
      const sortBy = orderedColumn ? sortFieldMap[orderedColumn.data] : null
      const sortDir = order ? order.dir : null

      try {
        // Fetch reviewed items for the current page
        const filterIdList = await api.getReviewedItemsWithFilter(page, filter, retailerName, sortBy, sortDir);

        const recordsTotal = filterIdList.meta ? filterIdList.meta.total : 0;

        const rows = (filterIdList.data || []).filter(item => item && item.data)

        // A deleted or otherwise unavailable seller comes back with no
        // retailer name, leaving a blank Retailer cell and a review no admin
        // can act on. Drop those rows instead of listing orphaned reviews.
        const visibleRows = rows.filter(item => {
          const attrs = item.data.attributes || {}
          const name = attrs.retailer || attrs.retailerName
          return typeof name === 'string' && name.trim() !== ''
        })

        // The count comes from the server, which still includes the orphans,
        // so discount the ones dropped here to keep the pager honest.
        const visibleTotal = Math.max(
          recordsTotal - (rows.length - visibleRows.length),
          0
        )

        callback({
          recordsTotal: visibleTotal,
          recordsFiltered: visibleTotal,
          data: visibleRows.map(item => {
            const attrs = item.data.attributes || {}
            return _.extend({}, attrs, {
              id: item.data.id,
              objectID: attrs.productId,
              internalId: attrs.productId,
            })
          })
        });
      } catch (error) {
        console.error('Error fetching diamonds:', error);
        // You can dispatch error actions here if necessary
        callback({ recordsTotal: 0, recordsFiltered: 0, data: [] });
      }
    },

    getRetailer(retailerId) {
      dispatch(getRetailer(retailerId));
    },

    clearDiamonds() {
      dispatch(clearDiamonds());
    },

    clearSelectedDiamond() {
      dispatch(clearSelectedDiamond());
    }
  };
};


export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ProductReview)
)
